import { useMutation, useQueryClient } from '@tanstack/vue-query';

import { API_QUERY_KEY_USER_ME } from '@/constants/api-query-key-constants.ts';
import { useApi } from '@/hooks/use-api';
import { validateApiSchema } from '@/lib/zod-helpers.ts';
import ApiError from '@/models/api/api-error.ts';
import { type User, UserSchema } from '@/schemas/users/user-schema.ts';
import type { TickerSymbol } from '@/types/homaio-ticker-enum';
import { PortfolioReportFrequency } from '@/types/users/user-portfolio-report-frequency-enum';

type UserUpdateVariable = {
  user: {
    waitlist?: TickerSymbol[];
    sources?: string[];
    ukaInvestmentIntent?: string;
    allowNewsletter?: boolean;
    allowPriceReports?: boolean;
    allowProductUpdates?: boolean;
    allowPortfolioReports?: boolean;
    portfolioReportFrequency?: PortfolioReportFrequency;
  };
  userId: string;
};

export function useUpdateUserMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<User, ApiError, UserUpdateVariable>({
    mutationFn: async (data: UserUpdateVariable) => {
      const response = await api.patch<User>(`/users/${data.userId}`, {
        ...data.user,
      });

      validateApiSchema(UserSchema, response.data);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [API_QUERY_KEY_USER_ME] });
    },
  });
}
